/*
 * Copyright (c) 2024, King Living
 * Swipeable Cart Item Component
 *
 * Wraps ProductItem to enable swipe-to-delete on mobile
 */

import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Box, Spinner, useBreakpointValue, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

const AXIS_LOCK_THRESHOLD_PX = 10;

const SwipeableItem = ({
  children,
  onSwipeDelete,
  showDivider = false,
}) => {
  const intl = useIntl();
  const isMobile = useBreakpointValue({base: true, lg: false});
  const styles = useMultiStyleConfig('CartFlyout', {});
  const containerRef = useRef(null);
  const onSwipeDeleteRef = useRef(onSwipeDelete);
  const gestureRef = useRef({
    axisLocked: false,
    deleting: false,
    isDragging: false,
    maxSwipe: 0,
    startX: 0,
    startY: 0,
    translateX: 0,
  });
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  onSwipeDeleteRef.current = onSwipeDelete;

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    const el = containerRef.current;

    if (!el) {
      return;
    }

    const gesture = gestureRef.current;

    const setTranslate = (next) => {
      if (next === gesture.translateX) {
        return;
      }

      gesture.translateX = next;
      setTranslateX(next);
    };

    const stopDrag = () => {
      gesture.isDragging = false;
      gesture.axisLocked = false;
      setIsDragging(false);
    };

    const handleTouchStart = (e) => {
      if (gesture.deleting) {
        return;
      }

      const touch = e.touches[0];

      gesture.maxSwipe = el.offsetWidth || window.innerWidth;
      gesture.startX = touch.clientX;
      gesture.startY = touch.clientY;
      gesture.axisLocked = false;
      gesture.isDragging = true;
      setIsDragging(true);
    };

    const handleTouchMove = (e) => {
      if (!gesture.isDragging || gesture.deleting) {
        return;
      }

      const touch = e.touches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;

      if (!gesture.axisLocked) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (absX < AXIS_LOCK_THRESHOLD_PX && absY < AXIS_LOCK_THRESHOLD_PX) {
          return;
        }

        if (absX <= absY) {
          stopDrag();

          return;
        }

        gesture.axisLocked = true;
      }

      e.preventDefault();

      if (deltaX < 0) {
        setTranslate(Math.max(deltaX, -gesture.maxSwipe));
      } else if (gesture.translateX < 0) {
        setTranslate(Math.min(deltaX, 0));
      }
    };

    const handleTouchEnd = (cancel = false) => {
      if (!gesture.isDragging) {
        gesture.axisLocked = false;

        return;
      }

      const offset = gesture.translateX;

      stopDrag();

      if (gesture.deleting) {
        return;
      }

      if (cancel || offset === 0) {
        if (offset !== 0) {
          setTranslate(0);
        }

        return;
      }

      if (Math.abs(offset) >= gesture.maxSwipe) {
        gesture.deleting = true;
        setIsDeleting(true);
        Promise.resolve(onSwipeDeleteRef.current?.()).catch(() => {
          gesture.deleting = false;
          gesture.translateX = 0;
          setTranslateX(0);
          setIsDeleting(false);
        });

        return;
      }

      setTranslate(0);
    };

    const touchBindings = [
      ['touchstart', handleTouchStart, {passive: true}],
      ['touchmove', handleTouchMove, {passive: false}],
      ['touchend', () => handleTouchEnd(), {passive: true}],
      ['touchcancel', () => handleTouchEnd(true), {passive: true}]
    ];

    touchBindings.forEach(([event, handler, options]) => {
      el.addEventListener(event, handler, options);
    });

    return () => {
      touchBindings.forEach(([event, handler]) => {
        el.removeEventListener(event, handler);
      });
    };
  }, [isMobile]);

  if (!isMobile) {
    return <Box>{children}</Box>;
  }

  const motionTransition = isDragging ? 'none' : 'transform 0.2s ease-out, width 0.2s ease-out';

  return (
    <Box className="swipeable-item" width="100%">
      <Box
        ref={containerRef}
        className="swipeable-item__track"
        overflow="hidden"
        position="relative"
        touchAction="pan-y"
        width="100%"
        {...(showDivider ? styles.swipeableItemTrackDivider : {})}
      >
        <Box
          {...styles.deleteButton}
          bottom={0}
          className="swipeable-item__delete-btn"
          overflow="hidden"
          position="absolute"
          right={{base: '-20px', lg: '0'}}
          top={0}
          transition={motionTransition}
          w={`${Math.abs(translateX)}px`}
          zIndex={1}
        >
          {intl.formatMessage({
            defaultMessage: 'Remove',
            id: 'cart_flyout.swipeable_item.delete_button.remove',
          })}
        </Box>

        <Box
          className="swipeable-item-content"
          paddingBottom="24px"
          position="relative"
          transform={translateX !== 0 ? `translateX(${translateX}px)` : undefined}
          transition={motionTransition}
          zIndex={2}
        >
          {children}
        </Box>

        {isDeleting && (
          <Box
            aria-busy="true"
            aria-live="polite"
            bg="whiteAlpha.700"
            className="swipeable-item__loading-overlay"
            {...styles.itemLoadingOverlay}
            zIndex={3}
          >
            <Spinner color="greyscale.100" size="md" thickness="2px" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

SwipeableItem.propTypes = {
  children: PropTypes.node.isRequired,
  onSwipeDelete: PropTypes.func.isRequired,
  showDivider: PropTypes.bool,
};

export default React.memo(SwipeableItem);
